import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PipelineTabs from './components/PipelineTabs';
import DomainsView from './components/DomainsView';
import ContactsView from './components/ContactsView';
import DraftsView from './components/DraftsView';
import ChatPanel from './components/ChatPanel';
import { DEFAULT_EMAIL_FILTERS, matchesEmailFilters } from './lib/filters';
import { applyAssistantActions } from './lib/assistantActions';
import { onCommand } from './lib/commandBus';
import { exportContacts, exportDomains, exportDraftEmails } from './lib/exports';
import {
  composePullHypothesis,
  isDomainHidden,
  isEmailHidden,
  loadAgentContext,
  loadCompanies,
  clearDrafts,
  draftIdForContact,
  draftsFromContacts,
  loadDraftTemplate,
  loadDrafts,
  loadEmailFilters,
  loadEmails,
  loadHiddenDomains,
  loadHiddenEmails,
  loadContactAppendMode,
  loadScrapePrefs,
  loadScrapeSession,
  saveAgentContext,
  saveDrafts,
  saveEmailFilters,
  saveScrapeSession,
  visibleCompanies,
} from './lib/storage';

function App() {
  const [view, setView] = useState('domains');
  const [companies, setCompanies] = useState(() => loadCompanies());
  const [scrapeSession, setScrapeSessionState] = useState(() => loadScrapeSession());
  const [emails, setEmails] = useState(() => loadEmails());
  const [hiddenDomains, setHiddenDomains] = useState(() => loadHiddenDomains());
  const [hiddenEmails, setHiddenEmails] = useState(() => loadHiddenEmails());
  const [draftById, setDraftById] = useState(() => {
    const map = {};
    loadDrafts().forEach((draft) => {
      if (draft?.id) map[draft.id] = draft;
    });
    return map;
  });
  const [emailFilters, setEmailFilters] = useState(() =>
    loadEmailFilters(DEFAULT_EMAIL_FILTERS)
  );
  const [domainsRunning, setDomainsRunning] = useState(false);
  const [emailsRunning, setEmailsRunning] = useState(false);

  const [chatMessages, setChatMessages] = useState([]);
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [agentContext, setAgentContextState] = useState(() => loadAgentContext());

  useEffect(() => {
    saveEmailFilters(emailFilters);
  }, [emailFilters]);

  useEffect(() => {
    saveAgentContext(agentContext);
  }, [agentContext]);

  const selectView = useCallback((next) => {
    if (next === 'drafts') {
      setView('emails');
      return;
    }
    setView(next);
  }, []);

  const setAgentContext = (next) => {
    setAgentContextState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      return saveAgentContext(value);
    });
  };

  const setScrapeSession = useCallback((next) => {
    setScrapeSessionState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      return saveScrapeSession(value);
    });
  }, []);

  const workingCompanies = useMemo(
    () => visibleCompanies(companies, hiddenDomains),
    [companies, hiddenDomains]
  );
  const workingDomains = useMemo(
    () => workingCompanies.map((c) => c.domain).filter(Boolean),
    [workingCompanies]
  );

  const filteredEmails = useMemo(
    () =>
      emails.filter((row) =>
        matchesEmailFilters(
          row,
          emailFilters,
          (domain) => isDomainHidden(domain, hiddenDomains),
          (email) => isEmailHidden(email, hiddenEmails)
        )
      ),
    [emails, emailFilters, hiddenDomains, hiddenEmails]
  );

  // Emails are always 1:1 with visible contacts — derived, never a separate list.
  const drafts = useMemo(
    () => draftsFromContacts(filteredEmails, draftById),
    [filteredEmails, draftById]
  );

  const exportCtxRef = useRef({});
  exportCtxRef.current = { workingCompanies, filteredEmails, drafts };

  useEffect(() => {
    return onCommand('download_csv', (payload = {}) => {
      const { target } = payload;
      const ctx = exportCtxRef.current;
      if (target === 'domains') exportDomains(ctx.workingCompanies);
      else if (target === 'contacts') exportContacts(ctx.filteredEmails);
      else if (target === 'emails') exportDraftEmails(ctx.drafts);
    });
  }, []);

  useEffect(() => {
    const visibleIds = new Set(filteredEmails.map((row) => draftIdForContact(row)));
    setDraftById((prev) => {
      let changed = false;
      const next = {};
      for (const [id, draft] of Object.entries(prev)) {
        if (visibleIds.has(id)) next[id] = draft;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [filteredEmails]);

  useEffect(() => {
    saveDrafts(drafts);
  }, [drafts]);

  const setDrafts = useCallback(
    (next) => {
      setDraftById((prev) => {
        if (typeof next === 'function') {
          const current = draftsFromContacts(filteredEmails, prev);
          const list = next(current);
          if (!Array.isArray(list)) return prev;
          if (!list.length) {
            clearDrafts();
            return {};
          }
          const map = { ...prev };
          list.forEach((draft) => {
            if (draft?.id) map[draft.id] = draft;
          });
          return map;
        }
        if (!Array.isArray(next)) return prev;
        if (!next.length) {
          clearDrafts();
          return {};
        }
        const map = { ...prev };
        next.forEach((draft) => {
          if (draft?.id) map[draft.id] = draft;
        });
        return map;
      });
    },
    [filteredEmails]
  );

  const actionCtxRef = useRef({});
  actionCtxRef.current = {
    setView: selectView,
    setEmailFilters,
    setHiddenDomains,
    setHiddenEmails,
    setEmails,
    setDrafts,
    setCompanies,
    setScrapeSession,
  };

  const applyActions = useCallback((actions) => {
    return applyAssistantActions(actions, actionCtxRef.current);
  }, []);

  // Listen for MCP / chat-broadcast actions
  useEffect(() => {
    const source = new EventSource('/api/assistant/events');
    source.onmessage = (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }
      if (payload.type === 'actions' && Array.isArray(payload.actions)) {
        // Skip echo of actions we just applied from in-panel chat (same tab).
        // Still apply MCP actions (source === 'mcp').
        if (payload.source === 'chat') return;
        applyActions(payload.actions);
      }
    };
    return () => source.close();
  }, [applyActions]);

  const buildUiState = useCallback(() => {
    const template = loadDraftTemplate();
    return {
      view,
      workingDomains,
      companiesCount: companies.length,
      scrapeSessionCount: scrapeSession.length,
      emailsCount: emails.length,
      filteredEmailsCount: filteredEmails.length,
      draftsCount: drafts.length,
      filteredDraftsCount: drafts.length,
      emailFilters,
      hiddenDomains,
      hiddenEmails,
      scrapePrefs: loadScrapePrefs({}),
      contactAppendMode: loadContactAppendMode(),
      draftTemplate: {
        subject: template.subject,
        body: template.body,
        pull: template.pull,
        pull_hypothesis: composePullHypothesis(template.pull),
      },
      domainsRunning,
      emailsRunning,
    };
  }, [
    view,
    workingDomains,
    companies.length,
    scrapeSession.length,
    emails.length,
    filteredEmails.length,
    drafts.length,
    emailFilters,
    hiddenDomains,
    hiddenEmails,
    domainsRunning,
    emailsRunning,
  ]);

  const sendChat = async (text) => {
    const nextMessages = [...chatMessages, { role: 'user', content: text }];
    setChatMessages(nextMessages);
    setChatBusy(true);
    setChatError(null);
    try {
      const response = await fetch('/api/assistant/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: nextMessages.map((m) => ({
            role: m.role,
            content: m.content,
          })),
          ui_state: buildUiState(),
          agent_context: agentContext,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || data.error || `HTTP ${response.status}`);
      }
      const notes = applyActions(data.actions || []);
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.reply || 'Done.',
          note: notes.length ? notes.join(' · ') : undefined,
        },
      ]);
    } catch (err) {
      setChatError(err.message || 'Chat failed');
    } finally {
      setChatBusy(false);
    }
  };

  return (
    <div className="h-full min-h-screen flex bg-white text-ink">
      <div className="flex-1 min-w-0 h-screen overflow-hidden flex flex-col">
        <PipelineTabs
          active={view === 'drafts' ? 'emails' : view}
          onSelect={selectView}
          domainCount={workingDomains.length}
          contactCount={filteredEmails.length}
          emailCount={filteredEmails.length}
          domainsRunning={domainsRunning}
          contactsRunning={emailsRunning}
        />
        <main className="flex-1 min-h-0 relative">
          <div className={view === 'domains' ? 'h-full' : 'hidden'}>
            <DomainsView
              companies={companies}
              onCompaniesChange={setCompanies}
              onScrapeSessionChange={setScrapeSession}
              hiddenDomains={hiddenDomains}
              onHiddenChange={setHiddenDomains}
              onRunningChange={setDomainsRunning}
              emails={emails}
            />
          </div>
          <div className={view === 'contacts' ? 'h-full' : 'hidden'}>
            <ContactsView
              domains={workingDomains}
              companies={workingCompanies}
              emails={emails}
              onEmailsChange={setEmails}
              hiddenEmails={hiddenEmails}
              onHiddenEmailsChange={setHiddenEmails}
              onRunningChange={setEmailsRunning}
              emailFilters={emailFilters}
              onEmailFiltersChange={setEmailFilters}
              filteredEmails={filteredEmails}
              onGoEmails={() => selectView('emails')}
            />
          </div>
          <div className={view === 'emails' || view === 'drafts' ? 'h-full' : 'hidden'}>
            <DraftsView
              emails={filteredEmails}
              drafts={drafts}
              onDraftsChange={setDrafts}
              onHideEmail={(email) =>
                setHiddenEmails((prev) =>
                  prev.includes(email) ? prev : [...prev, email]
                )
              }
            />
          </div>
        </main>
      </div>
      <ChatPanel
        messages={chatMessages}
        onSend={sendChat}
        busy={chatBusy}
        error={chatError}
        agentContext={agentContext}
        onAgentContextChange={setAgentContext}
      />
    </div>
  );
}

export default App;
